import React, {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  Droppable,
  DroppableProvided,
  OnDragEndResponder,
} from "react-beautiful-dnd";
import { useParams } from "react-router-dom";

import { LottieLoading } from "../../../../../../components/graphics/LottieLoading";
import { CheckSvg } from "../../../../../../components/svg/CheckSvg";
import { WorkDetailsModel } from "../../../../../../factories/work/model/work-details.model";
import { WorkTaskModel } from "../../../../../../factories/work/model/work-task.model";
import {
  useGetAutomationConditionsQuery,
  useGetAutomationJobTaskConditionsQuery,
  useGetAutomationsQuery,
} from "../../../../../../slices/AutomationSlice";
import {
  useGetWorkTasksQuery,
  useReorderWorkTasksMutation,
} from "../../../../../../slices/WorkTasksSlice";
import { IUser } from "../../../../../../types/types";
import { mapAutomationsResponseToItems } from "../../../automation/automation.mappers";
import { getAutomationSummaryParts } from "../../../automation/automation.summary";
import { useAutomationLookups } from "../../../automation/useAutomationLookups";
import { WorkTask } from "./WorkTask";
import { FiCheckSquare } from "react-icons/fi";

const reorder = (
  list: WorkTaskModel[],
  startIndex: number,
  endIndex: number,
) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);

  result.splice(endIndex, 0, removed);

  return result;
};

type Props = {
  work: WorkDetailsModel;
  users?: IUser[];
  onTasksStateChange?: (state: {
    allChecked: boolean;
    isIndeterminate: boolean;
  }) => void;
  onAllTasksCompleted?: () => Promise<void>;
};

export const WorkTasksList: FC<Props> = ({
  work,
  users,
  onTasksStateChange,
  onAllTasksCompleted,
}) => {
  const { workId } = useParams();
  const lookups = useAutomationLookups(work.serviceId);

  const { data, isLoading } = useGetWorkTasksQuery({
    workId: workId || "",
  });
  const { data: automationsData } = useGetAutomationsQuery(
    { workId: workId || "" },
    { skip: !workId },
  );
  const { data: workConditions = [] } = useGetAutomationConditionsQuery();
  const { data: jobTaskConditions = [] } =
    useGetAutomationJobTaskConditionsQuery();

  const [workTasks, setWorkTasks] = useState<WorkTaskModel[]>(data || []);
  const [reorderWorkTasks] = useReorderWorkTasksMutation();
  const automationTooltipsByWorkTaskId = useMemo(() => {
    const subtaskAutomations = mapAutomationsResponseToItems({
      data: automationsData,
      workName: work.name,
      options: lookups.options,
      workConditions,
      jobTaskConditions,
    }).filter((automation) => automation.scope === "subtask");

    return new Map(
      subtaskAutomations.map((automation) => [
        automation.subtaskId,
        getAutomationSummaryParts({
          automation,
          workName: work.name,
          options: lookups.options,
        }),
      ]),
    );
  }, [
    automationsData,
    jobTaskConditions,
    lookups.options,
    work.name,
    workConditions,
  ]);

  const total = workTasks.length;
  const completedCount = workTasks.filter((task) => task.isDone).length;

  const allChecked = total > 0 && completedCount === total;
  const noneChecked = completedCount === 0;
  const isIndeterminate = !allChecked && !noneChecked;

  const onDragEnd: OnDragEndResponder = (result) => {
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      workTasks,
      result.source.index,
      result.destination.index,
    );

    setWorkTasks(newItems);
    reorderWorkTasks(newItems.map((item) => item.id));
  };

  // SUB-014 (QA batch 17): the newest list, so a save that ends after another
  // subtask's tick doesn't put back the list as it was when it started (the
  // callback kept the old list and unticked the other subtask for a moment).
  const workTasksRef = useRef(workTasks);
  workTasksRef.current = workTasks;

  const handleTaskStatusChangeSuccess = useCallback(
    async (taskId: string, isDone: boolean) => {
      const current = workTasksRef.current;
      const nextWorkTasks = current.map((task) =>
        task.id === taskId ? { ...task, isDone } : task,
      );

      workTasksRef.current = nextWorkTasks;
      setWorkTasks(nextWorkTasks);

      if (nextWorkTasks.length && nextWorkTasks.every((task) => task.isDone)) {
        await onAllTasksCompleted?.();
      }
    },
    [onAllTasksCompleted],
  );

  useLayoutEffect(() => {
    setWorkTasks(data || []);
  }, [data]);

  useEffect(() => {
    onTasksStateChange?.({
      allChecked,
      isIndeterminate,
    });
  }, [allChecked, isIndeterminate, onTasksStateChange]);

  return (
    <div className={"w-full"}>
      {isLoading ? (
        <div>
          <LottieLoading />
        </div>
      ) : !workTasks.length ? (
        <div
          className={
            "flex min-h-[220px] flex-col items-center justify-center gap-3 px-6 pb-10 pt-8 text-center"
          }>
          <div
            className={
              "flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full bg-[#7C66F014] text-[#7C66F0]"
            }>
            <FiCheckSquare className={"h-6 w-6"} />
          </div>
          <div>
            <div className={"text-base font-semibold text-[#2B333C]"}>
              No subtasks yet
            </div>
            <div className={"mt-1 max-w-[420px] text-sm text-[#8B98A8]"}>
              Add subtasks to organize this work, or apply a subtask template
              to get started.
            </div>
          </div>
        </div>
      ) : (
        <div>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={"droppable"}>
              {(provided: DroppableProvided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className={"flex w-full max-w-full flex-col pb-3"}>
                  {workTasks.map((workTask, index) => (
                    <Draggable
                      key={workTask.id}
                      draggableId={workTask.id}
                      index={index}>
                      {(draggableProvided: DraggableProvided) => (
                        <WorkTask
                          workTask={workTask}
                          users={users || []}
                          work={work}
                          index={index}
                          length={workTasks.length}
                          provided={draggableProvided}
                          automationRule={automationTooltipsByWorkTaskId.get(
                            workTask.id,
                          )}
                          onStatusChangeSuccess={handleTaskStatusChangeSuccess}
                        />
                      )}
                    </Draggable>
                  ))}

                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </div>
  );
};
